import { FileText, Receipt, ClipboardList } from 'lucide-react';
import type { StudyFinanceCardRow } from '@/api/client';
import { fmtMoney } from '@/lib/money';
import { humanStatus, studyStatusBadgeClass } from '@/lib/humanise';
import { useMyPermissions } from '@/hooks/useMyPermissions';
import { CAN_GENERATE_INVOICE } from '@/lib/permissions';

/**
 * DREAM39 — one card per study, per Jawahar's 2026-07-31 comment. Mirrors
 * his mockup: `<ref> – <sponsor>`, `PI: <name> · Status: <badge> ·
 * Enrolled: <n>`, then the three-bucket visit/amount breakdown, then the
 * three shortcuts (Generate Invoice / View Past Invoices / Visit Billing
 * Detail). `completed_billable_count` always equals
 * `invoiced_count + to_be_invoiced_count` — the same partition his own
 * example numbers show (135 = 120 + 15).
 */
export function StudyFinanceCard({
  card,
  onGenerateInvoice,
  onViewPastInvoices,
  onViewBillingDetail,
}: {
  card: StudyFinanceCardRow;
  onGenerateInvoice: () => void;
  onViewPastInvoices: () => void;
  onViewBillingDetail: () => void;
}) {
  // finance.invoice is a single requireOperation on the backend, so the RBAC
  // grid decides; CAN_GENERATE_INVOICE is the fallback before it answers.
  const perms = useMyPermissions();
  return (
    <div className="card p-4 space-y-3">
      <div>
        <div className="flex items-center justify-between gap-2">
          <div className="font-semibold text-ink-primary truncate">
            {card.study_reference_number} – {card.sponsor_name}
          </div>
          <span className={studyStatusBadgeClass(card.status)}>{humanStatus(card.status)}</span>
        </div>
        <div className="mt-1 text-xs text-ink-muted">
          PI: {card.pi_name ?? '—'} · Enrolled: {card.enrolled_count}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-1.5 text-sm border-t border-surface-border pt-3">
        <div className="flex items-center justify-between">
          <span className="text-ink-muted">Completed billable visits</span>
          <span className="font-medium text-ink-primary">{card.completed_billable_count}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-ink-muted">Reported visits</span>
          <span className="font-medium text-ink-primary">
            {card.invoiced_count} · {fmtMoney(card.invoiced_amount_cents)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-ink-muted">To-be-reported visits</span>
          <span className="font-medium text-warning-700">
            {card.to_be_invoiced_count} · {fmtMoney(card.to_be_invoiced_amount_cents)}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-t border-surface-border pt-3">
        {perms.holdsOperation('finance.invoice', CAN_GENERATE_INVOICE) && (
          <button type="button" className="btn-primary text-xs" onClick={onGenerateInvoice}>
            <Receipt className="h-3.5 w-3.5" /> Generate Revenue Report
          </button>
        )}
        <button type="button" className="btn-secondary text-xs" onClick={onViewPastInvoices}>
          <FileText className="h-3.5 w-3.5" /> View Past Reports
        </button>
        <button type="button" className="btn-secondary text-xs" onClick={onViewBillingDetail}>
          <ClipboardList className="h-3.5 w-3.5" /> Visit Billing Detail
        </button>
      </div>
    </div>
  );
}
